import {
  DatabaseNotInitializedError,
  DatabaseInitializationError,
} from "@/domains/errors/kinds/database";
import { PGliteWithExtensions } from "../utils/db";
import { logger } from "@/modules/logger";
import { DATABASE_CONFIG } from "./config";
import { DatabaseFilesystemKind } from "@/domains/database/types/db";
import { cleanupObsoleteDatabases } from "./database-connection-manager.utils";
import pRetry from "p-retry";
import { TimeoutError } from "p-timeout";
import * as PGliteConnectionHandler from "./pg/pglite-connection-handler";
import * as MigrationRunner from "./pg/migration-runner";
import { uuidModule } from "@/modules/uuid";

export interface DatabaseConnectionOptions {
  filesystemKind: DatabaseFilesystemKind;
}

export interface GetInstanceOptions {
  spaceAccountId: string;
  connectionOptions: DatabaseConnectionOptions;
}

/**
 * Manages a single PGlite database connection.
 *
 * Key features:
 * - Single database instance
 * - Database names include the spaceAccountId for isolation
 */
export class DatabaseConnectionManager {
  // Static state management
  private static instance: DatabaseConnectionManager | null = null;

  // Instance state
  private database: PGliteWithExtensions | null = null;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private spaceAccountId: string;
  private readonly connectionOptions: DatabaseConnectionOptions;
  private activeCreationAttemptId: string | null = null;

  private constructor({
    spaceAccountId,
    options,
  }: {
    spaceAccountId: string;
    options: DatabaseConnectionOptions;
  }) {
    this.spaceAccountId = spaceAccountId;
    this.connectionOptions = options;
  }

  getFilesystemKind(): DatabaseFilesystemKind {
    return this.connectionOptions.filesystemKind;
  }

  /**
   * Get or create the connection manager instance.
   */
  static getInstance({
    spaceAccountId,
    connectionOptions,
  }: GetInstanceOptions): DatabaseConnectionManager {
    // Create instance if it doesn't exist
    if (!this.instance) {
      this.instance = new DatabaseConnectionManager({
        spaceAccountId,
        options: connectionOptions,
      });

      logger.info({
        message: "[DatabaseConnectionManager] Created new instance",
        info: { spaceAccountId },
      });
    }

    return this.instance;
  }

  /**
   * Get the current connection manager instance.
   * Returns null if no instance exists.
   */
  static getActiveInstance(): DatabaseConnectionManager | null {
    return this.instance;
  }

  /**
   * Clear the current instance and close the connection.
   * Primarily used for testing and cleanup scenarios.
   */
  static async clearInstance(): Promise<void> {
    if (this.instance) {
      logger.info({
        message: "[DatabaseConnectionManager] Clearing instance",
        info: { spaceAccountId: this.instance.spaceAccountId },
      });

      await this.instance.close();
      this.instance = null;
    }
  }

  /**
   * Initialize the database connection for this space account.
   * Creates a new IndexedDB database specific to the account.
   * Uses async locking to prevent race conditions from multiple concurrent calls.
   */
  async init(): Promise<void> {
    // If already initialized, return immediately
    if (this.initialized) {
      return;
    }

    // If initialization is in progress, wait for it to complete
    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    // Start initialization and store the promise
    this.initializationPromise = this.performInitialization();

    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Performs the actual database initialization.
   * Should only be called once per instance.
   */
  private async performInitialization(): Promise<void> {
    logger.info({
      message: "[DatabaseConnectionManager] Initializing database",
      info: { spaceAccountId: this.spaceAccountId },
    });

    const databaseName = this.getAccountDatabaseName();

    // Best-effort cleanup of obsolete databases for this device
    const _deletedNames = await cleanupObsoleteDatabases({
      currentDatabaseName: databaseName,
      filesystemKind: this.connectionOptions.filesystemKind,
    });

    // Phase 1: Create connection with retries + timeout per attempt
    logger.info({
      message: "[DatabaseConnectionManager] Creating database",
      info: { spaceAccountId: this.spaceAccountId, databaseName },
    });

    try {
      const createAttempt = async () => {
        const attemptId = uuidModule.generate();
        this.activeCreationAttemptId = attemptId;

        logger.info({
          message: "[DatabaseConnectionManager] Create attempt starting",
          info: { spaceAccountId: this.spaceAccountId, databaseName, attemptId },
        });

        return await PGliteConnectionHandler.create({
          databaseName,
          filesystemKind: this.connectionOptions.filesystemKind,
          timeoutMs: DATABASE_CONFIG.CREATE_TIMEOUT_MS,
          attemptId,
          isAttemptActive: id => this.activeCreationAttemptId === id,
        });
      };

      const db = (await pRetry(createAttempt, {
        retries: DATABASE_CONFIG.CREATE_MAX_RETRIES,
        minTimeout: DATABASE_CONFIG.CREATE_RETRY_MIN_MS,
        maxTimeout: DATABASE_CONFIG.CREATE_RETRY_MAX_MS,
        factor: DATABASE_CONFIG.CREATE_RETRY_FACTOR,
        randomize: DATABASE_CONFIG.CREATE_RETRY_RANDOMIZE,
        onFailedAttempt: err => {
          logger.warn({
            message: "[DatabaseConnectionManager] Create attempt failed",
            info: {
              spaceAccountId: this.spaceAccountId,
              databaseName,
              attempt: err.attemptNumber,
              retriesLeft: err.retriesLeft,
              errorName: err.name,
              errorMessage: err.message,
            },
          });
        },
      })) as PGliteWithExtensions;

      // Assign only if the attempt that produced this handle is the current one.
      if (!this.activeCreationAttemptId) {
        // Initialization was cancelled or failed; close the handle.
        try {
          await PGliteConnectionHandler.close(db);
        } catch (_closeError) {
          logger.warn({
            message: "[DatabaseConnectionManager] Failed to close db after cancelled create",
            info: { spaceAccountId: this.spaceAccountId, databaseName },
          });
        }
        this.database = null;
        throw new DatabaseInitializationError("Create phase cancelled", {
          phase: "creation",
          spaceAccountId: this.spaceAccountId,
          databaseName,
        });
      }

      this.database = db;

      logger.info({
        message: "[DatabaseConnectionManager] PGlite created",
        info: { spaceAccountId: this.spaceAccountId, databaseName },
      });
    } catch (error) {
      if (error instanceof TimeoutError) {
        // Mark attempt as inactive so late-resolving handles are considered stale and closed.
        this.activeCreationAttemptId = null;
        throw new DatabaseInitializationError("Create phase timed out", {
          phase: "creation",
          spaceAccountId: this.spaceAccountId,
          databaseName,
        });
      }
      this.activeCreationAttemptId = null;
      throw error;
    }

    // Phase 2: Run migrations with retries + timeout per attempt
    logger.info({
      message: "[DatabaseConnectionManager] Running migrations",
      info: { spaceAccountId: this.spaceAccountId, databaseName },
    });

    try {
      const migrateAttempt = async () => {
        const dbForMigration = this.database;
        if (!dbForMigration) {
          throw new DatabaseInitializationError("Database not available for migrations", {
            phase: "migration",
            spaceAccountId: this.spaceAccountId,
            databaseName,
          });
        }
        await MigrationRunner.run({ db: dbForMigration });
      };

      await pRetry(migrateAttempt, {
        retries: DATABASE_CONFIG.MIGRATION_MAX_RETRIES,
        minTimeout: DATABASE_CONFIG.MIGRATION_RETRY_MIN_MS,
        maxTimeout: DATABASE_CONFIG.MIGRATION_RETRY_MAX_MS,
        factor: DATABASE_CONFIG.MIGRATION_RETRY_FACTOR,
        randomize: DATABASE_CONFIG.MIGRATION_RETRY_RANDOMIZE,
        onFailedAttempt: err => {
          logger.warn({
            message: "[DatabaseConnectionManager] Migration attempt failed",
            info: {
              spaceAccountId: this.spaceAccountId,
              databaseName,
              attempt: err.attemptNumber,
              retriesLeft: err.retriesLeft,
              errorName: err.name,
              errorMessage: err.message,
            },
          });
        },
      });
    } catch (error) {
      // Ensure we close the created db on migration failure
      if (this.database) {
        try {
          await this.database.close();
        } catch (_closeError) {
          logger.warn({
            message: "[DatabaseConnectionManager] Failed to close db after migration failure",
            info: { spaceAccountId: this.spaceAccountId, databaseName },
          });
        }
        this.database = null;
      }

      if (error instanceof TimeoutError) {
        throw new DatabaseInitializationError("Migration phase timed out", {
          phase: "migration",
          spaceAccountId: this.spaceAccountId,
          databaseName,
        });
      }
      throw error;
    }

    this.initialized = true;

    logger.info({
      message: "[DatabaseConnectionManager] Database initialized successfully",
      info: { spaceAccountId: this.spaceAccountId, databaseName },
    });
  }

  /**
   * Get the database instance.
   * Throws if the database is not initialized.
   */
  getDatabase(): PGliteWithExtensions {
    if (!this.database || !this.initialized) {
      throw new DatabaseNotInitializedError({
        component: "DatabaseConnectionManager",
        action: "getDatabase",
        spaceAccountId: this.spaceAccountId,
      });
    }

    return this.database;
  }

  isReady(): boolean {
    return this.initialized && this.database !== null;
  }

  async close(): Promise<void> {
    if (this.database) {
      logger.info({
        message: "[DatabaseConnectionManager] Closing database connection",
        info: { spaceAccountId: this.spaceAccountId },
      });

      await this.database.close();
      this.database = null;
      this.initialized = false;
    }
  }

  /**
   * Get the raw database instance for testing purposes only.
   * @internal
   */
  getRawDatabase(): PGliteWithExtensions | null {
    return this.database;
  }

  private getAccountDatabaseName(): string {
    return `${DATABASE_CONFIG.NAME_PREFIX}-${DATABASE_CONFIG.DATABASE_VERSION}-${this.spaceAccountId}`;
  }
}
